import { QuestionCircleOutlined } from '@ant-design/icons';
import { L } from '@lib';
import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Mapping of portal routes to their corresponding documentation URLs
 */
const DOCS_URL_MAP: Record<string, string> = {
  '/calendar': 'https://umoto-co.gitbook.io/umoto.co-docs/calendar',
  '/orders': 'https://umoto-co.gitbook.io/umoto.co-docs/jobs',
  '/my-customers': 'https://umoto-co.gitbook.io/umoto.co-docs/customers',
  '/vehicle-histories': 'https://umoto-co.gitbook.io/umoto.co-docs/vehicle-histories',
  '/invoices': 'https://umoto-co.gitbook.io/umoto.co-docs/invoices',
  '/stock': 'https://umoto-co.gitbook.io/umoto.co-docs/parts-inventory',
  '/my-garage': 'https://umoto-co.gitbook.io/umoto.co-docs/settings/my-garage',
  '/my-services': 'https://umoto-co.gitbook.io/umoto.co-docs/settings/my-services',
  '/my-reports': 'https://umoto-co.gitbook.io/umoto.co-docs/settings/my-reports',
  '/tenant-concerns': 'https://umoto-co.gitbook.io/umoto.co-docs/settings/online-booking-setup',
  '/chat': 'https://umoto-co.gitbook.io/umoto.co-docs/chat',
};

/**
 * Returns the documentation URL for a given path, or undefined if no docs exist
 */
export const getDocsUrlForPath = (pathname: string): string | undefined => {
  // Check for exact match first
  if (DOCS_URL_MAP[pathname]) {
    return DOCS_URL_MAP[pathname];
  }
  
  // Check if the path starts with any of the mapped paths (for nested routes)
  for (const [route, url] of Object.entries(DOCS_URL_MAP)) {
    if (pathname.startsWith(route)) {
      return url;
    }
  }
  
  return undefined;
};

export interface IHowToDocsLinkProps {
  /** Override the URL instead of using the current route */
  url?: string;
  /** Custom label text (defaults to "How-to Docs") */
  label?: string;
  /** Font size in pixels */
  fontSize?: number;
}

/**
 * Displays a "How-to Docs" link with a question mark icon.
 * Automatically determines the documentation URL based on the current route,
 * or uses a custom URL if provided.
 * 
 * Only renders if a documentation URL is available for the current route.
 */
export const HowToDocsLink: React.FC<IHowToDocsLinkProps> = ({
  url,
  label = 'How to',
  fontSize = 13,
}) => {
  const location = useLocation();
  const docsUrl = url || getDocsUrlForPath(location.pathname);

  if (!docsUrl) {
    return null;
  }

  return (
    <a
      href={docsUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontSize,
        fontWeight: 400,
      }}
    >
      <QuestionCircleOutlined />
      {L(label)}
    </a>
  );
};

export default HowToDocsLink;
